import { getAppUrl } from "@src/appV2/environment/getAppUrl";
import { confirmResetPassword, resetPassword, signIn, signOut } from "aws-amplify/auth";

import { type CognitoUserCredential } from "./cognitoAuth";
import { fetchAuthSessionWithLock } from "./cognitoSessionLock";

function isUserAlreadyAuthenticatedException(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "UserAlreadyAuthenticatedException"
  );
}

export async function signInWithPassword(
  email: string,
  password: string
): Promise<CognitoUserCredential> {
  let result;
  try {
    result = await signIn({
      username: email,
      password,
      options: {
        authFlowType: "USER_PASSWORD_AUTH",
      },
    });
  } catch (error: unknown) {
    if (!isUserAlreadyAuthenticatedException(error)) {
      throw error;
    }

    await signOut();
    result = await signIn({
      username: email,
      password,
      options: {
        authFlowType: "USER_PASSWORD_AUTH",
      },
    });
  }

  if (result.nextStep.signInStep === "RESET_PASSWORD") {
    const error = new Error("Password reset required for the user");
    error.name = "PasswordResetRequiredException";
    throw error;
  }

  const session = await fetchAuthSessionWithLock();
  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error("Missing Cognito id token after password authentication");
  }

  return {
    idToken,
    accessToken: session.tokens?.accessToken?.toString(),
  };
}

export async function requestPasswordReset(email: string): Promise<void> {
  await resetPassword({
    username: email,
    options: {
      clientMetadata: {
        requester_url: `${getAppUrl()}/v2/reset-password`,
      },
    },
  });
}

export async function confirmPasswordReset(
  email: string,
  confirmationCode: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode,
    newPassword,
  });
}
